import React, { useState } from "react";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { Form, FormInstance, Input, Slider, Switch, Typography } from "antd";
import useTranslation from "utils/hooks/useTranslation";
import * as S from "../styled/form.styled";

type LayoutType = Parameters<typeof Form>[0]["layout"];

export interface IFormNewDocument {
  form: FormInstance;
  maskAllowLetters: boolean;
  maskAllowNumbers: boolean;
  maskAllowSpecialCharacters: boolean;
  maskMaxCharacters: number;
  maskMinCharacters: number;
  documentMaskAllowLetterActive: (...args) => void;
  documentMaskAllowNumberActive: (...args) => void;
  documentMaskAllowSpecialCharactersActive: (...args) => void;
  documentMaskMaxCharactersActive: (...args) => void;
  documentMaskMinCharactersActive: (...args) => void;
}
const { Title } = Typography;

const FormNewDocument = ({
  form,
  maskAllowLetters,
  maskAllowNumbers,
  maskAllowSpecialCharacters,
  maskMinCharacters,
  maskMaxCharacters,
  documentMaskAllowLetterActive,
  documentMaskAllowNumberActive,
  documentMaskAllowSpecialCharactersActive,
  documentMaskMinCharactersActive,
  documentMaskMaxCharactersActive,
}: IFormNewDocument) => {
  const [formLayout, setFormLayout] = useState<LayoutType>("vertical");
  const MINCHARACTERS = useTranslation({ id: "minimum" });
  const MAXCHARACTERS = useTranslation({ id: "maximum" });
  const marks = {
    0: MINCHARACTERS,
    100: MAXCHARACTERS,
  };

  const onFormLayoutChange = ({ layout }: { layout: LayoutType }) => {
    setFormLayout(layout);
  };

  const onChangeAllowLetter = (checkedMaskAllowLetter) => {
    documentMaskAllowLetterActive(checkedMaskAllowLetter);
  };

  const onChangeAllowSpecialCharacters = (checkedMaskAllowSpecialCharacters) => {
    documentMaskAllowSpecialCharactersActive(checkedMaskAllowSpecialCharacters);
  };

  const onChangeMaxMinCharacters = (charactersValue) => {
    documentMaskMinCharactersActive(charactersValue[0]);
    documentMaskMaxCharactersActive(charactersValue[1]);
  };

  const onChangeAllowNumber = (checkedMaskAllowNumber) => {
    documentMaskAllowNumberActive(checkedMaskAllowNumber);
  };

  function GetSlider() {
    return (
      <Slider
        range={{ draggableTrack: true }}
        marks={marks}
        tooltip={{ open: true }}
        onChange={onChangeMaxMinCharacters}
        value={[maskMinCharacters, maskMaxCharacters]}
        defaultValue={[0, 100]}
        getTooltipPopupContainer={() => document.querySelector(".ant-slider-step")}
      />
    );
  }

  return (
    <Form style={S.FormLayout} layout={"vertical"} form={form} initialValues={{ layout: formLayout }} onValuesChange={onFormLayoutChange} name="registers">
      <S.LayoutForm>
        <div className="item">
          <S.FormItem
            name="documentDescription"
            label={`${useTranslation({ id: "document" })}*`}
            hasFeedback
            rules={[
              {
                required: true,
                message: useTranslation({ id: "required_field" }),
              },
            ]}
          >
            <Input maxLength={50} showCount style={S.InputDocument} data-testid="documentDescription" />
          </S.FormItem>
          <S.MaskContainer>
            <div>
              <Title level={5} style={S.CheckDescription}>
                {useTranslation({ id: "allow_letter" })}
              </Title>
              <S.FormItem name="maskLetters">
                <Switch
                  className="switch-component"
                  checked={maskAllowLetters}
                  checkedChildren={<CheckOutlined />}
                  unCheckedChildren={<CloseOutlined />}
                  onChange={onChangeAllowLetter}
                />
              </S.FormItem>
            </div>
            <div>
              <Title level={5} style={S.CheckDescription}>
                {useTranslation({ id: "allow_number" })}
              </Title>
              <S.FormItem name="maskNumbers">
                <Switch
                  className="switch-component"
                  checked={maskAllowNumbers}
                  checkedChildren={<CheckOutlined />}
                  unCheckedChildren={<CloseOutlined />}
                  onChange={onChangeAllowNumber}
                />
              </S.FormItem>
            </div>
            <div>
              <Title level={5} style={S.CheckDescription}>
                {useTranslation({ id: "allow_special_characters" })}
              </Title>
              <S.FormItem name="maskSpecialCharacters">
                <Switch
                  className="switch-component"
                  checked={maskAllowSpecialCharacters}
                  checkedChildren={<CheckOutlined />}
                  unCheckedChildren={<CloseOutlined />}
                  onChange={onChangeAllowSpecialCharacters}
                />
              </S.FormItem>
            </div>
          </S.MaskContainer>
          <div>
            <Title level={5} style={S.CheckDescription}>
              {useTranslation({ id: "number_characters" })}
            </Title>
          </div>
          <S.MaskCharactersContainer>
            <S.FormItem name="maxCharacters">
              <div hidden>
                {maskMaxCharacters}
                {maskMinCharacters}
              </div>
              {GetSlider()}
            </S.FormItem>
          </S.MaskCharactersContainer>
        </div>
      </S.LayoutForm>
    </Form>
  );
};

export default FormNewDocument;
